/**
 * 视频目的(`briefPurpose`)8 值 —— **全站单一源**(波1,2026-07-25)。
 *
 * ═══ 为什么必须是单一源 ═══════════════════════════════════════════════════════
 * 波1 之前同一份「8 目的」散在三处各写一遍:`VideoBriefPanel` 的 chip 数组、
 * `catalog.ts` 的视频卡、`VideoCreatePage` 里 preset→purpose 的 if。三处都是字面量,
 * 改一处漏两处不会报错 —— 卡面写「产品带货」、chip 写「带货」、引擎按关键词匹配,
 * 静默降级成兜底 playbook,用户只会觉得「AI 没听懂」。故收成这一份。
 *
 * ═══ ★ label 是引擎契约,不是展示文案 ══════════════════════════════════════════
 * `label` 原文直接当 `briefPurpose` / `purpose` 上送,ai 侧 `select_playbook`
 * (`app/modules/video/domain/purpose_playbook.py`)按**关键词命中**取 playbook,
 * 未命中一律兜底 `_PRODUCT`(产品带货)。所以:
 *   - 卡面标题就用 label 本身(设计稿 `_design-mock/dept-video-hub.html` 也正是这 8 个词),
 *     不另立一套「友好文案」—— 两套文案必然漂移,而漂移的代价是引擎静默走错档。
 *   - 真要改字面(比如「招聘」改「招人宣传」),**必须同时保住 `engineKeyword` 子串**,
 *     否则那一档直接掉进兜底。`briefPurpose.test.ts` 逐条锁这件事。
 * 引擎侧 8 个 playbook 的 `key` 字段与这里的 label 逐字相同(已核对真代码),
 * console 8 目的 ↔ 引擎 8 playbook 一一对应。
 *
 * `key` 是 ascii 侧写,只用于:`sceneKey`(`video_<key>`,任务打标/场景筛)、
 * 主角图 `/scene-figures/<skin>/video_<key>.webp`、真产物样图 `/scene-cards/video_<key>.jpg`。
 * ★`narrative`/`hardsell` 两个 key 保持历史值不变 —— 打标口径不变,历史任务的场景标签
 * 不会失效,两套现成素材(主角图 + 真产物样图)也直接复用。
 */
import {
  Building2,
  CalendarCheck,
  Film,
  Filter,
  Megaphone,
  ShoppingBag,
  Sparkles,
  UserCheck,
  type LucideIcon,
} from "lucide-react";

export interface BriefPurposeDef {
  /** ascii 侧写:sceneKey / 主角图 / 样图文件名同源(不上送引擎)。 */
  key: string;
  /** ★引擎口径原文 —— 直接上送 `purpose`,同时就是卡面标题与 chip 文案。 */
  label: string;
  /**
   * 引擎 `select_playbook` 命中本档所依赖的关键词(`purpose_playbook.py::_PLAYBOOKS`)。
   * 改 label 时必须保住它;`产品带货` 无关键词 —— 它走的正是兜底 `_PRODUCT`。
   */
  engineKeyword: string | null;
  /** 引擎侧 playbook 标识(仅供人和测试对照,不参与运行时)。 */
  playbook: string;
  /** 一句话用途(卡面视觉权重最高的一行;≤17 汉字位,catalog.test.ts 锁)。 */
  usage: string;
  /** 卡面描述(第二行小字)。 */
  desc: string;
  /**
   * **叙事弧一行**(2026-09-12 hub 三段式重排)—— 目的卡面唯一那颗 chip 的文案。
   *
   * ★它是 `desc` 的骨架版,不是第二份真相:`desc` 那句是给创建页/引擎读的完整弧线
   * (「痛点 → 产品登场 → 用上之后的爽感 → 已融入生活,落点是想拥有。」),而卡面在
   * 254px 宽下装不下整句 —— chip 只留四个落点。两者必须讲同一条弧,改 playbook 时一起改。
   * ★不写进 `tags`:tags 是能力标签(「钩子」「转化」),弧是**这条片怎么走**,两码事;
   * 混在一起卡面会出现 4 颗 chip 抢眼,而真正帮用户判断的那行反而被稀释。
   */
  arc: string;
  /**
   * **「想拍什么」框的空态占位提问骨架**(2026-09-12 目的锁定 A4)—— 与 `arc` 并列的单一源。
   *
   * ★与 `briefSeed` 分工:`briefSeed` 是选目的后**种进框里的可编辑正文**(多行「拍给谁 / 卖点 /
   *   想让观众干嘛」问卷);本字段是**框清空后的 placeholder 幽灵字**(一句按本档 arc 写的示例)。
   *   目的锁定后备料台按目的定制的红利之一:每档一套提问方向,不再是一句通用示例。
   * ★逐条对着本档 `arc` 落点写(带货问痛点/卖点/CTA、招聘问岗位+邀请、公司介绍问最想让人
   *   记住的一句),两处讲同一条弧,改 playbook 时一起改。
   */
  intakePlaceholder: string;
  /** 卡面能力 chips(≤3)。readonly:整表是 `as const`,元组不可变(见 BRIEF_PURPOSES)。 */
  tags: readonly string[];
  /** 占位主角图 / 来源条用的图标。 */
  Icon: LucideIcon;
  /**
   * 点卡带进创建页的 brief 底稿(可改,不覆盖用户已输入的文案)。
   * ★逐条对着引擎 playbook 的 `arc` 落点写 —— 底稿与引擎要干的事说的是同一件事,
   * 否则用户照底稿写完,引擎按另一套弧线拍,白改一轮。
   * ★2026-08-10 起是「弧线一句 + 三槽 + 素材行」模板:400 电话真单实证用户会整段贴
   * 业务资料进来 —— 把「好文案的形状」(拍给谁/主打什么/要观众干嘛/资料归资料)摆在
   * 输入框里,是成本最低的引导。槽位是纯文本,引擎零改动。
   */
  briefSeed: string;
  /** 卡墙「最常用」推荐描边(只允许一张)。 */
  recommended?: true;
}

/**
 * 卡墙顺序 = 设计稿顺序(带货打头、剧情收尾)。
 * ★不做「按行业排序」:那是方案 §3 波4+ 路线图(千店千面,要读租户产品树),
 * 波1 没有那个输入 —— 卡墙副标也因此不许写「按你的行业排序」,不让 UI 撒谎。
 *
 * ★★ `as const satisfies` 而不是 `: readonly BriefPurposeDef[]`(codex review P2 修):
 * 写成类型标注会把每个 `label` 拓宽成 `string`,于是下面的 `BriefPurpose` 根本不是 8 值联合,
 * 而是 `string` —— `ScenePreset{kind:"video"; purpose}` 就能塞任意串通过编译,
 * 到 `videoSceneKeyOf` 再静默兜底成 `video_goods`,任务被**打上错标**(比没标更难查)。
 * `as const` 保住字面量,`satisfies` 仍然逐字段查形状:两样都要。
 */
export const BRIEF_PURPOSES = [
  {
    key: "goods",
    label: "产品带货",
    engineKeyword: null, // 兜底档:必须不含其余 7 档的任何关键词,否则会被劫持
    playbook: "_PRODUCT",
    usage: "把卖点讲成让人想下单的片",
    desc: "痛点 → 产品登场 → 用上之后的爽感 → 已融入生活,落点是想拥有。",
    arc: "痛点 → 登场 → 爽感 → 离不开",
    intakePlaceholder:
      "例如:洗衣凝珠,痛点是油渍反复洗不掉,卖点「深层去渍 · 15 秒溶解」,想让人下单囤货。",
    tags: ["钩子", "转化", "导演台可改"],
    Icon: ShoppingBag,
    briefSeed:
      "拍一条让人想下单的带货片:痛点场景 → 产品登场 → 用上之后的爽感 → 已经离不开它。\n拍给谁看:\n主打卖点(1-2 个):\n想让观众做什么(下单/咨询/进店):\n产品资料(选填,粘这里当素材):",
    recommended: true,
  },
  {
    key: "hardsell",
    label: "产品硬广",
    engineKeyword: "产品硬广",
    playbook: "_HARD_SELL",
    usage: "拍一条产品特写直给的硬广",
    desc: "最强卖点先砸,每个优势一个产品特写镜头 + 看得见的证明,结尾催下单。",
    arc: "亮相 → 卖点三连 → 证据 → 行动",
    intakePlaceholder:
      "例如:破壁机,最强三个卖点(转速 / 静音 / 自清洗)各配一个演示画面,附实测数据,结尾催下单。",
    tags: ["特写", "产品图首帧", "卖点直给"],
    Icon: Megaphone,
    briefSeed:
      "产品优势直给:每个卖点一个画面演示,配数字爆点与信任背书,结尾催下单。\n拍给谁看:\n最强的三个卖点:\n拿得出手的证明(数字/资质/对比):\n产品资料(选填,粘这里当素材):",
  },
  {
    key: "acquisition",
    label: "获客引流",
    engineKeyword: "获客",
    playbook: "_ACQUISITION",
    usage: "拉新钩子,把刷到的人变粉",
    desc: "第 1 秒就是钩子:困扰 → 产品时刻 → 收获感 →「这也可以是你」。",
    arc: "钩子 → 反差 → 价值 → 关注",
    intakePlaceholder:
      "例如:想吸宝妈粉,他们最愁辅食怎么搭,产品是 AI 食谱助手,看完关注领全套食谱。",
    tags: ["钩子", "冷启动", "涨粉"],
    Icon: Filter,
    briefSeed:
      "拍一条拉新引流片:第 1 秒钩子 → 扎心困扰 → 产品时刻 → 「这也可以是你」。\n拍给谁看(你想吸的粉):\n他们最扎心的困扰:\n想让观众做什么(关注/私信/留资):\n业务资料(选填,粘这里当素材):",
  },
  {
    key: "brand",
    label: "品牌宣传",
    engineKeyword: "品牌",
    playbook: "_BRAND",
    usage: "讲品牌故事,让人记住你",
    desc: "用一个能拍出来的具体事件兑现承诺,不喊「我们相信」那类空话。",
    arc: "起源 → 信念 → 见证 → 记住",
    intakePlaceholder:
      "例如:十年只做手工酿造,想让人记住「慢一点,更好」,有位老匠人凌晨翻缸的真实场景。",
    tags: ["故事", "情绪", "信任"],
    Icon: Sparkles,
    briefSeed:
      "拍一条品牌片:选一个能拍出来的具体场景,讲清它的代价,再用我们的能力解掉,收束到品牌精神。\n品牌是谁:\n想让人记住哪一点:\n有没有一个真实的故事或场景:\n品牌资料(选填,粘这里当素材):",
  },
  {
    key: "event",
    label: "活动预告",
    engineKeyword: "活动",
    playbook: "_EVENT",
    usage: "预热造势,把声量喊出去",
    desc: "悬念 → 线索揭示 → 期待拉满 → 时间地点参与方式,卡点剪辑。",
    arc: "悬念 → 亮点 → 福利 → 倒计时",
    intakePlaceholder:
      "例如:双 11 直播专场,11 月 10 日晚 8 点开播,进直播间抽免单,前 100 名半价。",
    tags: ["倒计时", "福利", "预热"],
    Icon: CalendarCheck,
    briefSeed:
      "拍一条活动预告片:悬念 → 亮点逐步揭示 → 期待拉满 → 时间地点参与方式。\n活动是什么:\n时间/地点/怎么参与:\n最大的亮点或福利:\n活动资料(选填,粘这里当素材):",
  },
  {
    key: "company",
    label: "公司介绍",
    engineKeyword: "公司介绍",
    playbook: "_COMPANY",
    usage: "一条片讲清公司是谁",
    desc: "行业问题 → 我们怎么解 → 成果证据 → 我们是谁,落点是信任。",
    arc: "是谁 → 做什么 → 凭什么 → 找我们",
    intakePlaceholder:
      "例如:做工业质检 AI,给制造业客户看,最想让人记住「漏检率降到 0.1%」,有头部客户案例。",
    tags: ["实力", "数字背书", "团队"],
    Icon: Building2,
    briefSeed:
      "拍一条公司介绍片:行业问题 → 我们怎么解 → 成果证据 → 我们是谁。\n公司做什么:\n拍给谁看(客户/伙伴/求职者):\n拿得出手的成果与数据:\n公司资料(选填,粘这里当素材):",
  },
  {
    key: "recruit",
    label: "招聘",
    engineKeyword: "招聘",
    playbook: "_RECRUIT",
    usage: "招人宣传,把岗位发出去",
    desc: "候选人视角的一天 → 团队高光瞬间 → 归属感 → 一句真诚邀请。",
    arc: "团队 → 日常 → 成长 → 来吧",
    intakePlaceholder:
      "例如:招前端工程师,最能打的是弹性工作 + 快速成长,团队氛围年轻,想让人投简历。",
    tags: ["薪资", "成长", "氛围"],
    Icon: UserCheck,
    briefSeed:
      "拍一条招聘片:候选人视角的一天 → 团队高光 → 归属感 → 真诚邀请。\n招什么岗位:\n薪资/成长/氛围里最能打的一点:\n想让候选人做什么(投递/咨询):\n岗位资料(选填,粘这里当素材):",
  },
  {
    key: "narrative",
    label: "剧情微故事",
    engineKeyword: "剧情",
    playbook: "_STORY",
    usage: "用小故事带出产品",
    desc: "三幕自由戏剧弧:建置 → 冲突转折 → 收束,不像广告的广告。",
    arc: "场景 → 冲突 → 转折 → 点题",
    intakePlaceholder:
      "例如:护眼台灯,主角是熬夜赶稿的学生,换灯后眼睛不再酸涩,记住那句「陪你到天亮」。",
    tags: ["剧情", "反转", "代入"],
    Icon: Film,
    briefSeed:
      "用 15~30 秒小故事讲我的产品:主角的麻烦 → 用上产品的转折 → 一句让人记住的收尾。\n产品是什么:\n主角是谁、遇到什么麻烦:\n想让观众记住哪一句:\n产品资料(选填,粘这里当素材):",
  },
] as const satisfies readonly BriefPurposeDef[];

/**
 * 目的 label **8 值字面量联合** —— `ScenePreset` / `FormState.briefPurpose` 共用的收窄类型。
 * (靠上面的 `as const` 才成立;退回类型标注会让它塌成 `string`,见那条注释。)
 */
export type BriefPurpose = (typeof BRIEF_PURPOSES)[number]["label"];

/**
 * 目的**分组**(2026-08-10 老板拍板「这一版视频只留四个卡」)—— 卡墙 8→4 的单一源。
 *
 * ═══ 组是卡面口径,档才是引擎契约 ═══════════════════════════════════════════════
 * 引擎 8 个 playbook / sceneKey 8 值 / 历史打标**全部不动**:组只存在于卡墙与目的行的
 * 呈现层,点组卡带 `defaultPurpose`(组内默认档)进创建页,上送引擎的仍是 8 值 label。
 * 拍法取**并集**(组内两档的拍法卡合并展示,选中哪张就按那张的所属档上送 ——
 * 引擎 selector 有「卡必须属当前目的」的闸,并集靠换档过闸而不是拆闸)。
 *
 * ★`assetKey` 复用组默认档的 `video_<key>`:三皮肤主角图 + 真产物样图两套素材零新增,
 *   e2e 里按 key 定位的钩子(如推荐卡 video_goods)也原样成立。
 * ★顺序即卡墙顺序:带货打头(推荐),「获客种草」按拍板放最后。
 */
export interface PurposeGroupDef {
  /** ascii 侧写(组级,只用于 React key/测试定位,不上送)。 */
  key: string;
  /** 卡面标题(组名纯展示,不碰引擎契约)。 */
  title: string;
  /** 一句话用途(卡面视觉权重最高的一行;≤17 汉字位,catalog.test.ts 锁)。 */
  usage: string;
  desc: string;
  tags: readonly string[];
  /** 组内两档(引擎口径 label,BRIEF_PURPOSES 子集;briefPurpose.test.ts 锁全覆盖不重不漏)。 */
  members: readonly [BriefPurpose, BriefPurpose];
  /** 点卡带进创建页的默认档。 */
  defaultPurpose: BriefPurpose;
  /** 素材 key(= `video_<默认档.key>`,复用现有两套图片素材)。 */
  assetKey: string;
  recommended?: true;
}

export const PURPOSE_GROUPS = [
  {
    key: "sell",
    title: "产品带货",
    usage: "把卖点讲成让人下单的片",
    desc: "种草带货或卖点直给的硬广,两种打法的拍法都在这。",
    tags: ["带货", "硬广", "转化"],
    members: ["产品带货", "产品硬广"],
    defaultPurpose: "产品带货",
    assetKey: "video_goods",
    recommended: true,
  },
  {
    key: "brandco",
    title: "品牌实力",
    usage: "讲品牌讲公司,让人记住你",
    desc: "品牌故事与公司介绍,树形象攒信任。",
    tags: ["品牌故事", "公司实力", "信任"],
    members: ["品牌宣传", "公司介绍"],
    defaultPurpose: "品牌宣传",
    assetKey: "video_brand",
  },
  {
    key: "eventhr",
    title: "活动招聘",
    usage: "活动造势,岗位招人",
    desc: "活动预告拉满期待,招聘片给一句真诚邀请。",
    tags: ["活动预热", "招聘"],
    members: ["活动预告", "招聘"],
    defaultPurpose: "活动预告",
    assetKey: "video_event",
  },
  {
    key: "growth",
    title: "获客种草",
    usage: "钩子拉新,故事种草",
    desc: "引流钩子把刷到的人变粉,或用小故事带出产品。",
    tags: ["钩子", "涨粉", "剧情"],
    members: ["获客引流", "剧情微故事"],
    defaultPurpose: "获客引流",
    assetKey: "video_acquisition",
  },
] as const satisfies readonly PurposeGroupDef[];

/**
 * **卡墙分组**(2026-09-12 hub 三段式重排,老板拍板)—— 8 张目的卡摆成两组的单一源。
 *
 * ═══ 与上面 `PURPOSE_GROUPS` 是两件事,别混 ═══════════════════════════════════
 *   `PURPOSE_GROUPS`(2 档一组 × 4)= **创建页**「换目的」那一行的选择粒度,
 *      点组卡进页带组默认档、组内拍法取并集。0912 这一批**不动它**(页内行为原样)。
 *   `VIDEO_WALL_GROUPS`(4 档一组 × 2)= **hub 卡墙**的区段口径:卡墙回到 8 张
 *      一档一卡,组只负责给两段各配一个区头(标题 + 副题 + 右侧「4 种」)。
 * 引擎契约仍然只有 8 值 label 这一份,两种分组都只是呈现层。
 *
 * ★成员写 label 而不是 key:卡片 preset 上送的就是 label,写 key 还得再查一次表,
 *   多一次映射就多一次漂移机会(`videoSceneKeyOf` 负责反向取 ascii 侧写)。
 * ★顺序即卡墙顺序:带货打头(最常用),「品牌与组织」在后。
 */
export interface VideoWallGroupDef {
  /** ascii 侧写,只用于 React key / `data-purpose-group` 定位,不上送。 */
  key: string;
  title: string;
  /** 区头副题:说**什么时候选这一组**,不说实现。 */
  sub: string;
  members: readonly BriefPurpose[];
}

export const VIDEO_WALL_GROUPS = [
  {
    key: "sellgrowth",
    title: "带货与获客",
    sub: "把产品卖出去、把人引进来的片 —— 选定即锁目的",
    members: ["产品带货", "产品硬广", "获客引流", "剧情微故事"],
  },
  {
    key: "brandorg",
    title: "品牌与组织",
    sub: "讲品牌、讲公司、办活动、招人的片 —— 选定即锁目的",
    members: ["品牌宣传", "公司介绍", "活动预告", "招聘"],
  },
] as const satisfies readonly VideoWallGroupDef[];

/**
 * 卡墙顺序的 8 值 label(两组摊平)—— `catalog.ts` 按它铸 8 张目的卡,
 * 卡墙渲染再按组切回两段。两处同源,墙上不会出现「组里有、墙上没有」的档。
 */
export const VIDEO_WALL_PURPOSES: readonly BriefPurpose[] = VIDEO_WALL_GROUPS.flatMap(
  (g) => [...g.members]
);

/** 当前目的档 → 所属组;非 8 值/空 → null。 */
export function purposeGroupOf(purpose?: string | null): PurposeGroupDef | null {
  if (!purpose || !isBriefPurpose(purpose)) return null;
  return PURPOSE_GROUPS.find((g) => (g.members as readonly string[]).includes(purpose)) ?? null;
}

const BY_LABEL: ReadonlyMap<BriefPurpose, BriefPurposeDef> = new Map(
  BRIEF_PURPOSES.map((p) => [p.label, p])
);

/** label → 定义;未知 label 返回 null(历史草稿里可能有已下线的目的字面量)。 */
export function briefPurposeOf(label?: string | null): BriefPurposeDef | null {
  return label && isBriefPurpose(label) ? (BY_LABEL.get(label) ?? null) : null;
}

const BY_KEY: ReadonlyMap<string, BriefPurposeDef> = new Map(BRIEF_PURPOSES.map((p) => [p.key, p]));

/**
 * ascii **key** → 定义(2026-09-12 目的锁定 A1)—— 创建页 URL `?usage=<key>` 的反查。
 *
 * ★用 `key`(ascii)而不是 `label`(中文)进 URL:query 里塞中文要百分号编码,直链/分享/
 *   日志里全是 `%E4%BA%A7...` 一坨,人肉不可读;`key` 本就是给文件名/sceneKey 用的 ascii 侧写,
 *   天然是 URL 安全的。未知 key(手改 URL / 下线档)→ null,创建页按无目的(自由态)渲染。
 */
export function briefPurposeByKey(key?: string | null): BriefPurposeDef | null {
  return key ? (BY_KEY.get(key) ?? null) : null;
}

/** 验型:是否 8 目的之一(`parseScenePreset` 与来源条共用)。 */
export function isBriefPurpose(v: unknown): v is BriefPurpose {
  return typeof v === "string" && BY_LABEL.has(v as BriefPurpose);
}

/**
 * 目的卡 sceneKey(任务打标 / 场景筛 / 素材文件名同源)。
 *
 * ★这里**没有**兜底分支:入参是 8 值字面量联合(编译期第一道门),外部 state 还要过
 * `parseScenePreset` 的 `isBriefPurpose`(运行期第二道门),而这张 map 与 label 联合同源。
 * 原来那句 `def ? def.key : "goods"` 看着稳,实际是把「未知目的」静默打成「产品带货」的标 ——
 * 错标比没标更难查(codex review P2)。宁可让它在类型上不可能发生。
 */
export function videoSceneKeyOf(label: BriefPurpose): string {
  return `video_${BY_LABEL.get(label)!.key}`;
}

/**
 * 同上,但入参是**运行时的任意串**(`form.briefPurpose` 那种,用户可改、草稿可带旧值):
 * 是 8 值之一 → sceneKey;不是 → `null`,**不打假标**。
 *
 * ★为什么需要这个而不是复用 `videoSceneKeyOf`:打标口径必须跟着**当前**目的走。
 * 目的 chip 在创建页是可改的,从「招聘」卡进来再换成「公司介绍」,若仍按进页那张卡打
 * `sceneKey=video_recruit`,这条片就被塞进错误的场景桶 —— 场景筛筛不到、列表上「来自哪张卡」
 * 显示的也是假的(codex review 第三轮 P2)。而非法/下线的目的串宁可不打标也不能打错标。
 */
export function videoSceneKeyFor(purpose?: string | null): string | null {
  const def = briefPurposeOf(purpose);
  return def ? `video_${def.key}` : null;
}
